import { navigate, parseHash, type Route } from "./router.ts";
import { recallTask } from "./board-cache.ts";

/**
 * Back, the app's way. iOS treats an edge swipe as browser history, which in a hash-routed app means
 * replaying every tab switch and sliding a screenshot of the old page across: wrong for this app,
 * where the only "back" that means anything is up one level (a task to its board, a form to its
 * list, Rewards to To Do). So:
 *  - a touch that starts at the left edge is claimed before the system can (preventDefault on a
 *    non-passive touchstart), unless it lands on a control, whose tap must still work;
 *  - a swipe from there of BACK_SWIPE_MIN_PX rightward, mostly horizontal, goes to parentRoute();
 *  - tab switches replace the history entry instead of pushing (TabBar), so the browser's own back
 *    (Android's button, a desktop shortcut) also goes up a level rather than through the tabs.
 */
export const BACK_EDGE_PX = 24;
export const BACK_SWIPE_MIN_PX = 70;
const BACK_SWIPE_MAX_DRIFT_PX = 50;

const TAB_ROUTES = new Set<Route["name"]>(["open", "done", "schedules", "upgrades"]);

export function isTabRoute(route: Route): boolean {
  return TAB_ROUTES.has(route.name);
}

/** The screen one level up from `route`, or null on a tab (there is no further back). */
export function parentRoute(route: Route): Route | null {
  switch (route.name) {
    case "task":
      return { name: recallTask(route.id)?.status === "done" ? "done" : "open" };
    case "newTask":
    case "hall":
    case "rewards":
    case "usage":
    case "trinkets":
    case "house":
    case "peon":
      return { name: "open" };
    case "newSchedule":
    case "schedule":
      return { name: "schedules" };
    case "newUpgrade":
    case "upgrade":
      return { name: "upgrades" };
    default:
      return null;
  }
}

function isControl(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("button, a, [role='button'], input, select, textarea, label, summary");
}

export function installBackSwipe(): () => void {
  let start: { x: number; y: number; id: number } | null = null;
  const onStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    if (t.clientX > BACK_EDGE_PX || isControl(e.target)) return;
    start = { x: t.clientX, y: t.clientY, id: t.identifier };
    e.preventDefault(); // ours, not the system's
  };
  const onEnd = (e: TouchEvent) => {
    if (!start) return;
    const t = Array.from(e.changedTouches).find((c) => c.identifier === start!.id);
    const begun = start;
    start = null;
    if (!t) return;
    const dx = t.clientX - begun.x;
    const dy = Math.abs(t.clientY - begun.y);
    if (dx < BACK_SWIPE_MIN_PX || dy > BACK_SWIPE_MAX_DRIFT_PX) return;
    const parent = parentRoute(parseHash(location.hash));
    if (parent) navigate(parent, true);
  };
  const onCancel = () => {
    start = null;
  };
  document.addEventListener("touchstart", onStart, { passive: false, capture: true });
  document.addEventListener("touchend", onEnd, { capture: true });
  document.addEventListener("touchcancel", onCancel, { capture: true });
  return () => {
    document.removeEventListener("touchstart", onStart, { capture: true });
    document.removeEventListener("touchend", onEnd, { capture: true });
    document.removeEventListener("touchcancel", onCancel, { capture: true });
  };
}
